import React, { useEffect, useRef, useState } from "react";
import { CandidateJobApplicationFilters } from "../../../Interface";
import { Button, Input, Select, Typography } from "antd";
import { ClearOutlined } from "@ant-design/icons";

const SORT_OPTIONS: {
  label: string;
  value: CandidateJobApplicationFilters["sort"];
}[] = [
  {
    label: "Recently Applied",
    value: "recent",
  },
  {
    label: "Oldest Applied",
    value: "oldest",
  },
  {
    label: "Profile Match Score (High to Low)",
    value: "profile.match.highest",
  },
  {
    label: "Profile Match Score (Low to High)",
    value: "profile.match.lowest",
  },
];

const PROFILE_SCREENING_OPTIONS: {
  label: string;
  value: CandidateJobApplicationFilters["profileMatchStatus"];
}[] = [
  {
    label: "Pending",
    value: "pending",
  },
  {
    label: "Qualified",
    value: "qualified",
  },
  {
    label: "Unqualified",
    value: "unqualified",
  },
];

const ASSESSMENT_OPTIONS: {
  label: string;
  value: CandidateJobApplicationFilters["assessmentStatus"];
}[] = [
  {
    label: "Action Required",
    value: "action_required",
  },
  {
    label: "Partially Qualified",
    value: "partially_qualified",
  },
  {
    label: "In Progress",
    value: "pending",
  },
  {
    label: "Unqualified",
    value: "unqualified",
  },
  {
    label: "Qualified",
    value: "qualified",
  },
];

const ONLINE_INTERVIEW_OPTIONS: {
  label: string;
  value: CandidateJobApplicationFilters["onlineInterviewStatus"];
}[] = [
  {
    label: "Slot Selection Pending",
    value: "slot.pending",
  },
  {
    label: "Upcoming",
    value: "upcoming",
  },
  {
    label: "Completed",
    value: "completed",
  },
];

const STATUS_OPTIONS: {
  label: string;
  value: CandidateJobApplicationFilters["status"];
}[] = [
  {
    label: "Applied",
    value: "applied",
  },
  {
    label: "Shortlisted",
    value: "shortlisted",
  },
  {
    label: "Interview",
    value: "interview",
  },
  {
    label: "Rejected",
    value: "rejected",
  },
  {
    label: "Hired",
    value: "hired",
  },
];

const SOURCE_OPTIONS: {
  label: string;
  value: CandidateJobApplicationFilters["source"];
}[] = [
  {
    label: "Candidate",
    value: "candidate",
  },
  {
    label: "Recruiter",
    value: "company",
  },
  {
    label: "Zoho Recruit",
    value: "zoho",
  },
  {
    label: "Lever Hire",
    value: "lever",
  },
];

interface CandidateJobApplicationsFilterMenuProp {
  filters: CandidateJobApplicationFilters;
  loading: boolean;
  handleClearFilter: () => void;
  handleFilter: (key: keyof CandidateJobApplicationFilters, value: any) => void;
}

const CandidateJobApplicationsFilterMenu: React.FC<
  CandidateJobApplicationsFilterMenuProp
> = ({ filters, loading, handleClearFilter, handleFilter }) => {
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const {
    search,
    sort,
    source,
    profileMatchStatus,
    assessmentStatus,
    onlineInterviewStatus,
    status,
  } = filters;

  const handleClearAll = () => {
    handleClearFilter();
    setSearchText(undefined);
  };

  const hasAnyFilters = !!(
    sort ||
    search ||
    source ||
    profileMatchStatus ||
    assessmentStatus ||
    onlineInterviewStatus ||
    status
  );

  const handleSearch = (value: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    handleFilter("search", value);
  };

  const handleSort = (value: string | undefined) => {
    handleFilter("sort", value || null);
  };

  const handleProfileMatchStatus = (value: string | undefined) => {
    handleFilter("profileMatchStatus", value || null);
  };

  const handleAssessmentStatus = (value: string | undefined) => {
    handleFilter("assessmentStatus", value || null);
  };

  const handleOnlineInterviewStatus = (value: string | undefined) => {
    handleFilter("onlineInterviewStatus", value || null);
  };

  const handleStatus = (value: string | undefined) => {
    handleFilter("status", value || null);
  };

  const handleSource = (value: string | undefined) => {
    handleFilter("source", value || null);
  };

  useEffect(() => {
    setSearchText(search || undefined);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchText !== (search || "")) {
      debounceRef.current = setTimeout(() => {
        handleFilter("search", searchText || null);
      }, 500);
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, search]);

  return (
    <div className="candidate-application-filter-menu">
      <div className="candidate-application-filter-menu__top">
        <Input.Search
          enterButton
          size="middle"
          disabled={loading}
          placeholder="Search by name, email or job post"
          allowClear
          onSearch={handleSearch}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Select
          size="middle"
          allowClear
          placeholder="Sort by"
          options={SORT_OPTIONS}
          value={sort}
          onChange={handleSort}
          className="candidate-application-filter-menu__sort"
        />
        <Button
          size="middle"
          disabled={!hasAnyFilters || loading}
          loading={loading}
          onClick={handleClearAll}
          icon={<ClearOutlined />}
        >
          Clear
        </Button>
      </div>
      <div className="candidate-application-filter-menu__filters">
        <label className="candidate-application-filter-menu__filter-item">
          <Typography.Text strong className="candidate-application-filter-menu__label">Profile Screening</Typography.Text>
          <Select
            size="middle"
            allowClear
            placeholder="All"
            options={PROFILE_SCREENING_OPTIONS}
            value={profileMatchStatus}
            onChange={handleProfileMatchStatus}
          />
        </label>
        <label className="candidate-application-filter-menu__filter-item">
          <Typography.Text strong className="candidate-application-filter-menu__label">Assessment</Typography.Text>
          <Select
            size="middle"
            allowClear
            placeholder="All"
            options={ASSESSMENT_OPTIONS}
            value={assessmentStatus}
            onChange={handleAssessmentStatus}
          />
        </label>
        <label className="candidate-application-filter-menu__filter-item">
          <Typography.Text strong className="candidate-application-filter-menu__label">Online Interview</Typography.Text>
          <Select
            size="middle"
            allowClear
            placeholder="All"
            options={ONLINE_INTERVIEW_OPTIONS}
            value={onlineInterviewStatus}
            onChange={handleOnlineInterviewStatus}
          />
        </label>
        <label className="candidate-application-filter-menu__filter-item">
          <Typography.Text strong className="candidate-application-filter-menu__label">Status</Typography.Text>
          <Select
            size="middle"
            allowClear
            placeholder="All"
            options={STATUS_OPTIONS}
            value={status}
            onChange={handleStatus}
          />
        </label>
        <label className="candidate-application-filter-menu__filter-item">
          <Typography.Text strong className="candidate-application-filter-menu__label">Source</Typography.Text>
          <Select
            size="middle"
            allowClear
            placeholder="All"
            options={SOURCE_OPTIONS}
            value={source}
            onChange={handleSource}
          />
        </label>
      </div>
    </div>
  );
};

export default CandidateJobApplicationsFilterMenu;
